<template>
  <div class="bridge-selection-container">            
    <el-transfer
      v-model="selectedBridges"
      :key="`transfer-${selectedBridges.length}`"
      :data="bridgeOptions"
      :titles="['可选桥梁', '已选桥梁']"
      :props="{
        key: 'id',
        label: 'bridgeName'
      }"
      :render-content="renderBridgeItem"
      :target-order="'original'"
      filterable
      filter-placeholder="请输入桥梁名称"
      class="bridge-transfer"
      @change="handleBridgeChange">
      <template slot="left-footer">
        <div class="transfer-tip">
          <i class="el-icon-info"></i> 点击选择要关联的桥梁
        </div>
      </template>
      <template slot="right-footer">
        <div class="transfer-info">
          <i class="el-icon-success"></i> 已选择 {{ selectedBridges.length }} 个桥梁
          <div v-if="hasMissingBridges" class="missing-bridges-info">
            <i class="el-icon-warning-outline"></i> 
            <span>有桥梁在当前桥梁列表中不存在，但仍将保留关联</span>
          </div>
        </div>
      </template>
    </el-transfer>
  </div>
</template>

<script>
import { getUserBridges } from '@/api/bridge'

export default {
  name: 'BridgeTransfer',
  
  props: {
    value: {
      type: Array,
      default: () => []
    },
    bridgeList: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    },
    userId: {
      type: [Number, String],
      default: null
    },
    autoLoad: {
      type: Boolean,
      default: false
    }
  },
  
  data() {
    return {
      bridgeOptions: [],
      selectedBridges: [],
      localLoading: false
    }
  },
  
  computed: {
    // 检查是否有缺失的桥梁
    hasMissingBridges() {
      return this.bridgeOptions.some(bridge => bridge.isMissing);
    },
    
    // 合并外部和内部的loading状态
    isLoading() {
      return this.loading || this.localLoading;
    }
  },
  
  watch: {
    // 监听外部传入的桥梁列表变化
    bridgeList: {
      immediate: true,
      handler(newList) {
        if (newList && newList.length > 0) {
          this.initBridgeOptions(newList);
        }
      }
    },
    
    // 监听外部传入的选中值变化
    value: {
      immediate: true,
      handler(newValue) {
        if (Array.isArray(newValue)) {
          // 防止无限循环，先判断是否有变化
          const newIds = newValue.map(id => Number(id)).sort((a, b) => a - b).join(',');
          const currentIds = this.selectedBridges.map(id => Number(id)).sort((a, b) => a - b).join(',');
          
          // 只有当值真正发生变化时才更新
          if (newIds !== currentIds) {
            // 确保所有值都是数字类型
            this.selectedBridges = newValue.map(id => Number(id));
            
            // 检查是否有缺失的桥梁
            if (this.bridgeOptions.length > 0 && this.selectedBridges.length > 0) {
              this.$nextTick(() => {
                this.checkAndAddMissingBridges(this.selectedBridges);
              });
            }
          }
        } else {
          this.selectedBridges = [];
        }
      }
    },
    
    // 监听userId变化，自动加载用户桥梁
    userId: {
      handler(newUserId) {
        if (this.autoLoad && newUserId) {
          this.fetchUserBridges(newUserId);
        }
      },
      immediate: true
    }
  },
  
  methods: {
    // 初始化桥梁选项
    initBridgeOptions(bridges) {
      this.bridgeOptions = bridges.map(bridge => ({
        id: Number(bridge.id),
        bridgeName: bridge.name || bridge.bridgeName,
        key: Number(bridge.id)
      }));
      
      // 检查已选中的桥梁
      if (this.selectedBridges.length > 0) {
        this.checkAndAddMissingBridges(this.selectedBridges);
      }
    },
    
    // 检查并添加缺失的桥梁
    checkAndAddMissingBridges(bridgeIds) {
      if (!bridgeIds || bridgeIds.length === 0) return;
      
      // 优化性能：使用Set来存储ID以加快查找速度
      const availableIds = new Set(this.bridgeOptions.map(b => Number(b.id)));
      const existingMissingIds = new Set(
        this.bridgeOptions.filter(b => b.isMissing).map(b => Number(b.id))
      );
      
      // 找出所有在选中列表中但不在桥梁选项中的ID，且不是已知的缺失ID
      const missingIds = bridgeIds.filter(id => {
        const numId = Number(id);
        return !availableIds.has(numId) && !existingMissingIds.has(numId);
      });
      
      if (missingIds.length > 0) {
        console.log('发现新的缺失桥梁ID:', missingIds);
        
        // 为每个缺失的ID创建一个虚拟桥梁选项
        const missingBridgeOptions = missingIds.map(id => ({
          id: Number(id),
          bridgeName: `桥梁ID-${id} (不在当前列表中)`,
          key: Number(id),
          isMissing: true // 标记为缺失的桥梁
        }));
        
        // 将这些虚拟桥梁添加到选项中
        this.bridgeOptions = [...this.bridgeOptions, ...missingBridgeOptions];
      }
    },
    
    // 处理桥梁选择变化
    handleBridgeChange() {
      // 确保所有值都是数字类型
      const selectedIds = this.selectedBridges.map(id => Number(id));
      
      // 触发事件，通知父组件值已更改
      this.$emit('input', selectedIds);
      this.$emit('change', selectedIds);
    },
    
    // 自定义桥梁渲染方法
    renderBridgeItem(h, option) {
      return h('span', [
        h('span', option.bridgeName),
        option.isMissing ? h('el-tag', {
          props: {
            size: 'mini',
            type: 'warning'
          },
          style: {
            marginLeft: '5px'
          }
        }, '缺失') : null
      ]);
    },
    
    // 获取用户关联的桥梁
    async fetchUserBridges(userId) {
      if (!userId) return;
      
      this.localLoading = true;
      
      try {
        const res = await getUserBridges(userId);
        
        if (res.code === 200) {
          // 提取桥梁ID，确保转换为数字类型
          const bridgeIds = res.data.map(bridge => 
            Number(bridge.bridgeId || bridge.bridgeId || bridge.id || 0)
          ).filter(id => id > 0); // 过滤掉无效ID
          
          console.log('获取到的用户桥梁ID:', bridgeIds);
          
          // 设置选中的桥梁ID
          this.selectedBridges = bridgeIds;
          
          // 通知父组件
          this.$emit('input', bridgeIds);
          this.$emit('loaded', bridgeIds);
          
          // 检查缺失的桥梁
          if (this.bridgeOptions.length > 0) {
            this.$nextTick(() => {
              this.checkAndAddMissingBridges(bridgeIds);
            });
          }
        } else {
          console.error('获取用户桥梁关联失败:', res.message);
          this.$emit('load-error', res.message || '获取用户桥梁关联失败');
        }
      } catch (error) {
        console.error('获取用户桥梁关联出错:', error);
        this.$emit('load-error', error.message || '获取用户桥梁关联失败');
      } finally {
        this.localLoading = false;
      }
    },
    
    // 重置选中的桥梁
    reset() {
      this.selectedBridges = [];
      this.$emit('input', []);
    }
  }
}
</script>

<style scoped>
.bridge-selection-container {
  padding: 10px 0;
  overflow: visible;
}

.bridge-transfer {
  width: 100%;
  display: flex;
  justify-content: space-between;
}

.bridge-transfer >>> .el-transfer-panel {
  width: 45%;
  min-width: 300px;
}

.bridge-transfer >>> .el-transfer-panel__body {
  height: 300px;
}

.bridge-transfer >>> .el-transfer-panel__list {
  height: 260px;
}

.bridge-transfer >>> .el-transfer__buttons {
  padding: 0 10px;
}

.transfer-tip {
  color: #606266;
  font-size: 12px;
  margin-bottom: 10px;
}

.transfer-info {
  color: #606266;
  font-size: 12px;
}

.missing-bridges-info {
  color: #f56c6c;
  font-size: 12px;
  margin-top: 5px;
}
</style> 